/**
 * Stable pseudonymisation for the diagnostics report.
 *
 * The report is written to be handed to a stranger — attached to a public
 * GitHub issue — so it must not carry the reporter's addresses, account mail
 * or device names. Blanking them out (`***`) would make it useless at the same
 * time: half of every diagnosis is "did these two lines talk about the SAME
 * device", and a report where every address reads `***` cannot answer that.
 *
 * So each distinct value gets a stable marker instead: the same address is
 * `address-1` everywhere in the report, a second one is `address-2`. Structure
 * that carries meaning survives — a private address stays recognisably private,
 * so "these two devices sit in different subnets" is still visible.
 *
 * Markers are stable within one adapter run, not across runs: two reports the
 * same user exports minutes apart share them, two exports across a restart do
 * not. The report header says so, otherwise a reader comparing two files would
 * assume `address-1` is the same device in both.
 *
 * Ordering matters and is the caller's job: redact, then pseudonymise, THEN
 * cap. The size cap turns an oversized body into a plain truncated string, and
 * neither key-based redaction nor marker replacement can reach inside one —
 * a real address would ship in the truncated remainder.
 */

/** IPv4 in dotted form. Bounded to 1-3 digits per group so it can't run away in a long string. */
const IPV4_RE = /\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/g;

/**
 * IPv6, in the only two shapes that are actually addresses: eight full groups,
 * or a `::`-compressed form. Deliberately NOT "two or more hex groups" — that
 * earlier version matched `AA:BB:CC` and turned a short device id into an
 * address marker. A run of colon-separated hex is not an address unless it has
 * all eight groups or the `::` compression; anything shorter is left alone,
 * because a false positive here silently corrupts the report while a miss only
 * leaves an already-rare string in place.
 */
const IPV6_RE = /\b(?:[0-9a-f]{1,4}:){7}[0-9a-f]{1,4}\b|\b(?:[0-9a-f]{1,4}:)+:(?:[0-9a-f]{1,4}(?::[0-9a-f]{1,4})*)?/gi;

/** Anything shaped like a mail address — the Govee account mail can surface in login error bodies. */
const EMAIL_RE = /\b[^\s@<>"']+@[^\s@<>"']+\.[a-z]{2,}\b/gi;

/**
 * Govee device ids: colon-separated hex in 6 or 8 groups (`AA:BB:…:11`). Kept
 * partly readable rather than replaced — see {@link Anonymiser.deviceId}.
 */
const DEVICE_ID_RE = /\b(?:[0-9a-f]{2}:){5,7}[0-9a-f]{2}\b/gi;

/**
 * Whether an IPv4 address is in a private range — worth preserving as a fact.
 *
 * @param ip The address to classify
 */
function isPrivateIpv4(ip: string): boolean {
  const p = ip.split(".").map(n => parseInt(n, 10));
  if (p.length !== 4 || p.some(n => !Number.isInteger(n) || n < 0 || n > 255)) {
    return false;
  }
  if (p[0] === 10 || p[0] === 127) {
    return true;
  }
  if (p[0] === 192 && p[1] === 168) {
    return true;
  }
  if (p[0] === 172 && p[1] >= 16 && p[1] <= 31) {
    return true;
  }
  // Link-local (169.254/16) — what a device shows when DHCP failed, which is
  // itself a diagnosis, so it must stay distinguishable from a routed address.
  return p[0] === 169 && p[1] === 254;
}

/**
 * Assigns and remembers markers. One instance per adapter run (the diagnostics
 * collector owns it), so every buffer and every report share the same mapping.
 */
export class Anonymiser {
  private readonly markers = new Map<string, string>();
  private readonly counters = new Map<string, number>();

  /**
   * The marker for one value in one category, minted on first sight.
   *
   * @param kind Marker prefix, e.g. `address` or `device`
   * @param value The real value to stand in for
   */
  private marker(kind: string, value: string): string {
    const key = `${kind} ${value}`;
    const existing = this.markers.get(key);
    if (existing !== undefined) {
      return existing;
    }
    const next = (this.counters.get(kind) ?? 0) + 1;
    this.counters.set(kind, next);
    const assigned = `${kind}-${next}`;
    this.markers.set(key, assigned);
    return assigned;
  }

  /**
   * Marker for an IP address. Private and link-local addresses are marked as
   * such, so "both devices are local" and "this one answered from the internet"
   * stay readable.
   *
   * @param ip The address as it appeared
   */
  ip(ip: string): string {
    const scope = IPV4_RE.test(ip) || ip.includes(".") ? (isPrivateIpv4(ip) ? "local" : "public") : "local";
    IPV4_RE.lastIndex = 0;
    return this.marker(`address-${scope}`, ip);
  }

  /**
   * A device id keeps its last four hex characters and loses the rest. Those
   * four are already the folder name in the object tree (`h61be_1d6f`), so the
   * report stays matchable against a user's screenshot without carrying the
   * full hardware id.
   *
   * @param id The device id as it appeared
   */
  deviceId(id: string): string {
    const compact = id.replace(/:/g, "");
    const tail = compact.slice(-4).toLowerCase();
    return `id-…${tail}`;
  }

  /**
   * Marker for a user-chosen device name. Names routinely carry a room or a
   * person ("Lisa's bedroom"), so they never travel — but the same name must
   * map to the same marker everywhere, or the report stops being followable.
   *
   * @param name The device name as the user set it
   */
  deviceName(name: string): string {
    return this.marker("device", name);
  }

  /**
   * Replace every address, mail address and device id inside a free-text
   * string. Log lines and foreign error bodies are the places these hide.
   *
   * @param text Arbitrary text
   * @param names Device names to replace as well (they have no detectable shape)
   */
  text(text: string, names: readonly string[] = []): string {
    let out = text
      .replace(EMAIL_RE, m => this.marker("mail", m))
      .replace(IPV4_RE, m => this.ip(m))
      .replace(DEVICE_ID_RE, m => this.deviceId(m));
    // IPv6 last: its pattern also matches a run of hex groups, so device ids
    // must already be gone or they would be swallowed as addresses.
    out = out.replace(IPV6_RE, m => this.ip(m));
    for (const name of names) {
      if (name && name.length >= 3 && out.includes(name)) {
        out = out.split(name).join(this.deviceName(name));
      }
    }
    return out;
  }

  /**
   * Walk a already-cloned structure and pseudonymise every string in it, keys
   * included — a Govee response can key a map by device id.
   *
   * @param value Freshly cloned value, safe to mutate
   * @param names Device names to replace inside strings
   * @returns The value with every string pseudonymised
   */
  walk(value: unknown, names: readonly string[] = []): unknown {
    if (typeof value === "string") {
      return this.text(value, names);
    }
    if (Array.isArray(value)) {
      return value.map(v => this.walk(v, names));
    }
    if (value && typeof value === "object") {
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        out[this.text(k, names)] = this.walk(v, names);
      }
      return out;
    }
    return value;
  }
}
